/* eslint-disable node/no-unsupported-features/node-builtins */
import React, {Suspense, useEffect, useState} from 'react';
import * as Sentry from '@sentry/react';
import {axiosAuthInterceptor} from '@taopix/taopix-core';
import tooltipsTemplate from '../../CoreCustoms/tooltipTemplate.html';
import i18n from '../../i18n/i18n';
import {ThemeTooltip, LoadingOverlay} from '@taopix/taopix-design-system';
import {HashRouter as Router, Route, Routes} from 'react-router-dom';
import SharePreview from '../Views/Share/SharePreview';
import {AssetMetaInterface, BootOptions, TaopixCore} from '@taopix/taopix-core/dist/types/types';
import AppView from '../Views/AppView';
import AssistantView from '../Views/AssistantView';
import UploadFromDeviceView from '../Views/UploadFromDeviceView';
import Collections from '../Product/Collections';
import LayoutSelector from '../Product/LayoutSelector';
import Products from '../Product/Products';
import DesignerView from '../Views/DesignerView';
import ProductsView from '../Views/ProductsView';

export type TaopixDesignerProps = {
  hash: string;
  showThemeTips: boolean;
  isDev: boolean;
  uploadFromPhoneToken: string;
};

const Taopix = ({core}: {core: TaopixCore}) => {
  const [booted, setBooted] = useState(false);
  const [isDev, setIsDev] = useState(false);
  const [showThemeTips, setShowThemeTips] = useState(false);
  const [ufpToken, setUpfToken] = useState('');

  const [bootOptions, setBootOptions] = useState<BootOptions>({
    ...(window as any).config,
    ref: undefined,
    theme: undefined,
    custom: {
      tooltipsTemplate: tooltipsTemplate,
      pageOverlayColour: '#0079db55',
      overlayColor: '#0079db55',
      overlayBreakPoint: '350',
    },
    memoryMonitor: false,
    assets: [],
  });

  useEffect(() => {
    const token = PubSub.subscribe(
      'SWITCH_COLLECTION',
      (msg: string, payload: {ref: string; theme: string; assets: AssetMetaInterface[]}) => {
        if (payload.ref === bootOptions.ref) {
          return;
        }

        // reset the boot flag
        setBooted(false);

        // set the sentry sessionref tag
        Sentry.setTag('sessionRef', payload.ref);

        setBootOptions(current => {
          return {
            ...current,
            ...payload,
          };
        });
      }
    );

    return () => {
      PubSub.unsubscribe(token);
    };
  }, [bootOptions]);

  // set the boot options if we have a ref
  useEffect(() => {
    const params = new URLSearchParams(document.location.search);
    const ref = params.get('ref') ?? '';
    if ('' === ref) {
      // window.history.pushState({}, '', '#/products');
      return;
    }

    setShowThemeTips(params.get('showThemeTips') === 'true');
    setIsDev(params.get('tpxdev') === 'true');
    setUpfToken(params.get('payload') ?? '');

    // only here for dev
    setBooted(false);

    // break up the ref into it's own params
    const settings = new URLSearchParams(atob(ref));

    // set the sentry sessionref tag
    Sentry.setTag('sessionRef', settings.get('ref'));

    // set the boot properties
    setBootOptions(current => {
      return {
        ...current,
        ref: String(settings.get('ref')),
        theme: String(settings.get('theme')),
      };
    });
  }, []);

  // boot the core if we have a project ref
  useEffect(() => {
    if (!bootOptions.ref) {
      return setBooted(false);
    }

    // boot the core now we have a project ref
    core.create(bootOptions);

    // let the app know it's booted
    setBooted(true);
  }, [bootOptions, core]);

  // misc setup
  useEffect(() => {
    if (!booted) {
      return;
    }

    const loader = window.Taopix.Core.get('loader');
    const server = window.Taopix.Core.get('server');

    // set up the auth interceptor for refreshing tokens
    axiosAuthInterceptor({
      url: (window as any).config.baseURL,
      storage: server.getStorage(),
      ref: bootOptions.ref as string,
    });

    // set the correct language, after project init
    loader.on('onInit', response => {
      /* Respond to the language sent by backend */
      i18n.changeLanguage(response.locale.language).then(() => {
        localStorage.setItem('i18nextLng', response.locale.language);
      });
    });

    // the payload param will only exist on upload from phone &
    // contains the upload token and designerId
    if ('' !== ufpToken) {
      const server = window.Taopix.Core.get('server');
      let token = ufpToken;
      if (token.endsWith('/')) {
        token = ufpToken.slice(0, ufpToken.length - 1);
      }
      const data = new URLSearchParams(atob(token));
      // set the designer uuid in storage to prevent session termination
      server.getStorage().setItem(`${bootOptions.ref}-uuid`, data.get('designerId'));
    }
  }, [booted, bootOptions, ufpToken]);

  return (
    <Suspense
      fallback={
        <LoadingOverlay
          id={'suspense-overlay'}
          open={true}
          usePortal={false}
          overlayColour={'transparent'}
        />
      }
    >
      {showThemeTips && <ThemeTooltip />}
      <Router basename="/">
        <Routes>
          <Route
            element={
              <DesignerView
                booted={booted}
                sessionRef={bootOptions.ref}
                isDev={isDev}
                core={core}
              />
            }
          >
            <Route index element={<AppView />} />
            <Route path="/assistant" element={<AssistantView />} />
            <Route path="/upload" element={<UploadFromDeviceView />} />
            <Route path="/preview" element={<SharePreview />} />
          </Route>
          <Route path="/products/:groupCode" element={<ProductsView core={core} />}>
            <Route index element={<Collections />} />
            <Route path=":collectionCode" element={<Products />} />
            <Route path="layouts/:layoutCode" element={<LayoutSelector />} />
          </Route>
        </Routes>
      </Router>
    </Suspense>
  );
};

export default Taopix;
